// ─────────────────────────────────────────────
//  teamDirectory.styles — the inline style objects for the Team directory.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Split out of TeamDirectory.js only to keep both files under the 500-line
//  house limit — this is the same module-scope `const s = {}` every page in
//  this repo ends with, moved next door. There are no CSS modules here; the
//  only classes involved live in src/index.css and carry hover and motion.
//
//  ⚠️ `lobby.brass` (#9a7b3f) IS 3.9:1 AND MAY ONLY BE A RULE OR A BORDER.
//  Every warm word on this surface uses `lobby.brassInk` (#7a5c26, 5.6:1 on
//  linen). The trap is written down in theme/tokens.js; it is repeated here
//  because this file is where someone reaches for a colour.
//
//  ⚠️ THE SEARCH FIELD IS 16px AND MUST STAY THERE. Any text input under 16px
//  makes iOS Safari zoom the whole page on focus, and the page then pans
//  sideways for the rest of the session. This app has shipped that bug before.
//
//  ⚠️ A ROW HAS TWO ACTIONS NOW — Call and Email — so `actions` wraps and the
//  email link may run onto a second line. Do not give either one a fixed width
//  or `whiteSpace: nowrap`: an email address is long and unpredictable, and the
//  first thing a nowrap pill does on a 360px phone is push the row sideways.
// ─────────────────────────────────────────────
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;
const { lobby } = color;

// The directory is a scrolling list under a pinned search field, so the shell
// is a flex column and only the results region scrolls. A page-level scrollbar
// would carry the search box off the top exactly when it is needed most.
const styles = {
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    background: lobby.linen,
    color: color.text.primary,
    fontFamily: font.family,
  },
  // Inside the native shell the frame already pads; cancel it so the warm
  // ground reaches every edge, then re-apply it within. Same trick, and the
  // same reason, as MobileLanding.
  bodyInShell: { margin: `-${space.md}px`, padding: `${space.md}px ${space.md}px 0` },

  head: {
    width: "100%",
    maxWidth: 760,
    margin: "0 auto",
    padding: `${space.lg}px ${space.lg}px ${space.md}px`,
    boxSizing: "border-box",
    flexShrink: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: "2.4px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  title: {
    margin: `${space.xs}px 0 0`,
    fontSize: 28,
    fontWeight: font.weight.semibold,
    lineHeight: font.lineHeight.tight,
    letterSpacing: "-0.3px",
    color: color.text.primary,
  },
  rule: { width: 56, height: 2, background: lobby.brass, borderRadius: radius.full, margin: `${space.sm}px 0 0` },
  provenance: {
    margin: `${space.sm}px 0 0`,
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
  },

  // ── The search bar: the page's primary control, not a corner affordance ──
  searchForm: { margin: `${space.md}px 0 0` },
  searchLabel: {
    display: "block",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    letterSpacing: ".6px",
    textTransform: "uppercase",
    color: lobby.brassInk,
    marginBottom: 6,
  },
  field: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    background: lobby.plaque,
    border: `1px solid ${lobby.brass}`,
    borderRadius: radius.lg,
    padding: `0 ${space.sm}px 0 ${space.md}px`,
    minHeight: 52,
  },
  fieldIcon: { fontSize: 16, lineHeight: 1, flexShrink: 0 },
  input: {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    // ⚠️ 16px minimum — see the header. Do not shrink this.
    fontSize: 16,
    fontFamily: "inherit",
    color: color.text.primary,
    padding: `${space.md}px 0`,
  },
  clear: {
    flexShrink: 0,
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    borderRadius: radius.full,
    fontSize: 16,
    color: color.text.secondary,
    fontFamily: "inherit",
  },

  chips: { display: "flex", flexWrap: "wrap", gap: space.sm, margin: `${space.sm}px 0 0` },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 44,
    padding: `0 ${space.md}px`,
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.full,
    fontSize: font.size.body,
    fontFamily: "inherit",
    color: color.text.secondary,
  },
  // Pressed carries TWO signals — the fill and the ✓ glyph — so the state is
  // never conveyed by colour alone.
  chipOn: {
    background: color.accent.primary,
    borderColor: color.accent.primary,
    color: "#fff",
    fontWeight: font.weight.bold,
  },

  count: {
    margin: `${space.md}px 0 0`,
    fontSize: font.size.body,
    fontWeight: font.weight.semibold,
    color: color.text.secondary,
    minHeight: 20,
  },

  // ── The scrolling results region ──
  results: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
  resultsInner: {
    width: "100%",
    maxWidth: 760,
    margin: "0 auto",
    padding: `0 ${space.lg}px ${space["3xl"]}px`,
    boxSizing: "border-box",
  },

  group: { margin: `${space.lg}px 0 0` },
  groupHeading: { margin: 0, fontSize: font.size.body },
  groupToggle: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    width: "100%",
    minHeight: 48,
    textAlign: "left",
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.lg,
    padding: `0 ${space.md}px`,
    fontFamily: "inherit",
    color: color.text.primary,
  },
  caret: { width: 14, flexShrink: 0, color: lobby.brassInk, fontSize: 11 },
  groupLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: font.weight.bold,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
  },
  groupCount: { fontSize: font.size.label, fontWeight: font.weight.bold, color: lobby.brassInk, flexShrink: 0 },

  list: { listStyle: "none", margin: `${space.sm}px 0 0`, padding: 0 },
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.sm,
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.lg,
    padding: `${space.sm}px ${space.md}px`,
    marginBottom: 6,
  },
  // minWidth:0 is load-bearing — without it a long surgeon note refuses to
  // shrink and pushes the call button off the side of the row.
  rowMain: { flex: "1 1 200px", minWidth: 0 },
  rowName: { display: "block", fontSize: 15, fontWeight: font.weight.semibold, lineHeight: font.lineHeight.snug },
  // ⚠️ THERE IS NO BADGE STYLE HERE, AND THAT IS THE POINT. A row is a name, a
  // phone and an email. No campus chip and no role pill belongs on it: the
  // section heading already says the role, and the badge styles were deleted
  // with the profile join that fed them rather than left behind as an
  // invitation. See the header of components/TeamDirectory.js.
  //
  // `note` is the ONE secondary fact a row's KIND carries — a surgeon's
  // specialty, a facility's address. Secondary to the name by size and colour,
  // and never alarm-coloured: an address is not a warning.
  note: {
    display: "block",
    marginTop: 4,
    fontSize: font.size.label,
    lineHeight: font.lineHeight.snug,
    color: color.text.secondary,
    overflowWrap: "anywhere",
  },
  actions: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, flexShrink: 0 },
  call: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 44,
    padding: `0 ${space.md}px`,
    borderRadius: radius.full,
    border: `1px solid ${color.accent.primary}`,
    background: lobby.linen,
    color: color.accent.primary,
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  // The email link. Deliberately QUIETER than the Call pill — reaching a
  // clinician mid-day means the phone; email is the fallback — but still a real
  // 44px target with its own accessible name ("Email Clara Ibarra").
  //
  // ⚠️ IT WRAPS. An address is long and unpredictable and a nowrap pill pushes
  // the row off the side of a phone; `overflowWrap: anywhere` is what keeps a
  // 40-character address inside a 360px screen.
  mail: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 44,
    maxWidth: "100%",
    padding: `0 ${space.sm}px`,
    background: "transparent",
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.full,
    // brassInk (#7a5c26), never brass — 5.6:1 on linen, 6.1:1 on plaque.
    color: lobby.brassInk,
    fontSize: font.size.label,
    fontWeight: font.weight.semibold,
    fontFamily: "inherit",
    textDecoration: "none",
    overflowWrap: "anywhere",
  },
  // A person with no usable number is a plain statement of fact, in the same
  // ink as everything else. Not amber, not red — see the alarm-colour rule.
  noPhone: { fontSize: font.size.label, color: color.text.secondary, whiteSpace: "nowrap" },

  more: {
    display: "block",
    width: "100%",
    minHeight: 44,
    marginTop: 6,
    background: "transparent",
    border: `1px dashed ${lobby.brass}`,
    borderRadius: radius.lg,
    color: lobby.brassInk,
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    fontFamily: "inherit",
  },

  notice: {
    maxWidth: 460,
    margin: `${space.xl}px auto`,
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.lg,
    padding: space.lg,
    textAlign: "center",
  },
  noticeTitle: {
    margin: `0 0 ${space.sm}px`,
    fontSize: font.size.heading,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight.tight,
  },
  noticeBody: {
    margin: 0,
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
  },
  noticeCta: {
    marginTop: space.md,
    minHeight: 44,
    padding: `0 ${space.lg}px`,
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.md,
    fontSize: font.size.body,
    fontWeight: font.weight.semibold,
    fontFamily: "inherit",
  },
};

export default styles;
